import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Camera,
  Car,
  CheckCircle2,
  Clock,
  Droplets,
  MapPin,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { AvaliarLavagem } from './avaliar-lavagem';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { formatarDataHora } from '@/lib/format';
import { supabase } from '@/lib/supabase';

/**
 * /lavagem/:id — detalhe de uma lavagem do cliente.
 * Acessivel via deep link (notif "lavagem concluida" leva aqui).
 *
 * Repaginado no segundo ciclo: hero com gradient + status grande + foto em
 * primeiro plano (não escondida no fim). Quando CONCLUIDA, momento celebratório.
 */

interface Lavagem {
  seq_lavagem: number;
  cod_status: string;
  cod_modalidade: string;
  num_placa_lida: string | null;
  qtd_segundos_duracao: number | null;
  dta_inicio: string;
  dta_fim: string | null;
  des_url_evidencia_foto: string | null;
  cod_motivo_cancelamento: string | null;
  val_lavagem: number | null;
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  CONCLUIDA: 'success',
  EM_ANDAMENTO: 'info',
  INICIADA: 'info',
  CANCELADA: 'danger',
  FALHA: 'danger',
  PENDENTE: 'warning',
};

export function LavagemDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const lavagem = useQuery({
    queryKey: ['lavagem-detalhe', id, user?.id],
    queryFn: async () => {
      if (!id) throw new Error('ID ausente');
      // RLS garante que so retorna se for do cliente atual
      const { data, error } = await supabase
        .from('tab_lavagens')
        .select(
          'seq_lavagem, cod_status, cod_modalidade, num_placa_lida, qtd_segundos_duracao, dta_inicio, dta_fim, des_url_evidencia_foto, cod_motivo_cancelamento, val_lavagem',
        )
        .eq('seq_lavagem', Number(id))
        .maybeSingle();
      if (error) throw error;
      return data as Lavagem | null;
    },
    enabled: !!id && !!user,
  });

  if (lavagem.isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-56 rounded-3xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
    );
  }

  if (lavagem.error || !lavagem.data) {
    return (
      <div className="space-y-4 p-4">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground -ml-2 h-8 px-2">
          <Link to="/lavagens">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              {lavagem.error
                ? `Erro: ${(lavagem.error as Error).message}`
                : 'Lavagem não encontrada ou você não tem permissão para visualizá-la.'}
            </p>
            <Button variant="outline" asChild className="mt-4">
              <Link to="/lavagens">Ver minhas lavagens</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const l = lavagem.data;
  const isDone = l.cod_status === 'CONCLUIDA';
  const isRunning = l.cod_status === 'EM_ANDAMENTO' || l.cod_status === 'INICIADA';
  const isFailed = l.cod_status === 'CANCELADA' || l.cod_status === 'FALHA';

  return (
    <div className="relative min-h-screen overflow-hidden p-4 pb-24">
      {/* Blob decorativo */}
      <div
        aria-hidden
        className={`octa-blob pointer-events-none absolute -left-32 -top-32 h-72 w-72 opacity-30 ${
          isDone
            ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
            : isFailed
              ? 'bg-gradient-to-br from-rose-400 to-pink-500'
              : 'bg-octa-gradient'
        }`}
      />

      <div className="relative z-10 mx-auto max-w-md space-y-5">
        {/* Botão voltar */}
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="text-muted-foreground octa-fade-in-up -ml-2 h-8 px-2"
        >
          <Link to="/lavagens">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Voltar
          </Link>
        </Button>

        {/* Card hero — Apple Wallet feel */}
        <Card variant="wallet" className="octa-scale-in p-0">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/15 blur-3xl"
          />
          <div
            aria-hidden
            className="bg-white/12 pointer-events-none absolute -bottom-16 -left-8 h-44 w-44 rounded-full blur-2xl"
          />

          <div className="relative z-10 space-y-5 p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85">
                  Lavagem #{l.seq_lavagem}
                </p>
                <h2 className="mt-1 text-3xl font-extrabold leading-tight tracking-tight">
                  {l.num_placa_lida ?? '—'}
                </h2>
              </div>
              <StatusBadge
                variant={STATUS_VARIANT[l.cod_status] ?? 'neutral'}
                dot
                pulse={isRunning}
                className="bg-white/20 text-white ring-white/30 backdrop-blur"
              >
                {l.cod_status.replace('_', ' ')}
              </StatusBadge>
            </div>

            {/* Status icon gigante celebratório */}
            <div className="flex flex-col items-center gap-3 py-3">
              <div
                className={`octa-success-pop flex h-20 w-20 items-center justify-center rounded-3xl bg-white/20 ring-2 ring-white/30 backdrop-blur ${
                  isRunning ? 'octa-pulse-halo' : ''
                }`}
              >
                {isDone ? (
                  <Sparkles className="h-10 w-10 drop-shadow" />
                ) : isFailed ? (
                  <XCircle className="h-10 w-10 drop-shadow" />
                ) : isRunning ? (
                  <Droplets className="h-10 w-10 animate-pulse drop-shadow" />
                ) : (
                  <CheckCircle2 className="h-10 w-10 drop-shadow" />
                )}
              </div>
              <p className="text-center text-sm font-semibold text-white/90">
                {isDone
                  ? 'Lavagem concluída! Brilhando.'
                  : isRunning
                    ? 'Sua lavagem está rolando agora.'
                    : isFailed
                      ? 'Não foi dessa vez.'
                      : 'Aguardando início.'}
              </p>
            </div>

            {/* Divider perfurado */}
            <div className="relative -mx-6">
              <div className="border-t-2 border-dashed border-white/30" />
              <div className="bg-background absolute -left-1 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full" />
              <div className="bg-background absolute -right-1 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full" />
            </div>

            {/* Detalhes em grid */}
            <div className="grid grid-cols-3 gap-3 pt-1 text-center">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-white/80">
                  Início
                </p>
                <p className="mt-1 text-xs font-bold leading-tight">
                  {formatarDataHora(l.dta_inicio).split(' ').slice(-1)[0]}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-white/80">
                  Duração
                </p>
                <p className="mt-1 text-xs font-bold">
                  {l.qtd_segundos_duracao ? `${Math.round(l.qtd_segundos_duracao / 60)} min` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-white/80">
                  Modo
                </p>
                <p className="mt-1 text-xs font-bold uppercase leading-tight">{l.cod_modalidade}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Foto evidência — em primeiro plano se houver */}
        {l.des_url_evidencia_foto && (
          <Card
            variant="elevated"
            className="octa-fade-in-up overflow-hidden p-0"
            style={{ animationDelay: '0.1s' }}
          >
            <div className="relative">
              <img
                src={l.des_url_evidencia_foto}
                alt="Evidencia da lavagem"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur">
                <Camera className="h-3 w-3" />
                Foto registrada
              </div>
            </div>
          </Card>
        )}

        {/* Detalhes complementares */}
        <Card variant="elevated" className="octa-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                <Car className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className="text-overline text-muted-foreground uppercase">Placa</p>
                <p className="font-bold tracking-wider">{l.num_placa_lida ?? '—'}</p>
              </div>
            </div>
            <div className="octa-divider" />
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                <Clock className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className="text-overline text-muted-foreground uppercase">Quando</p>
                <p className="font-bold">
                  {formatarDataHora(l.dta_inicio)}
                  {l.dta_fim && (
                    <span className="text-muted-foreground font-medium">
                      {' '}
                      → {formatarDataHora(l.dta_fim)}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="octa-divider" />
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className="text-overline text-muted-foreground uppercase">Tipo</p>
                <p className="font-bold">Modalidade: {l.cod_modalidade}</p>
              </div>
            </div>
            {l.val_lavagem != null && l.val_lavagem > 0 && (
              <>
                <div className="octa-divider" />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-semibold">Valor</span>
                  <span className="text-octa-gradient text-2xl font-extrabold tracking-tight">
                    {Number(l.val_lavagem).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {l.cod_motivo_cancelamento && (
          <Card
            variant="default"
            className="octa-fade-in-up border-rose-200 bg-rose-50/60 dark:border-rose-900 dark:bg-rose-950/30"
          >
            <CardContent className="flex items-start gap-3 p-4">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300" />
              <div>
                <p className="text-sm font-bold text-rose-900 dark:text-rose-100">
                  Lavagem cancelada
                </p>
                <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-200">
                  Motivo: {l.cod_motivo_cancelamento}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Avaliação 1-5 estrelas (so se CONCLUIDA) */}
        {isDone && (
          <div className="octa-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <AvaliarLavagem seqLavagem={l.seq_lavagem} />
          </div>
        )}
      </div>
    </div>
  );
}
